import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { FiX } from 'react-icons/fi';
import { useGetHomeData, type UserDiscoverModel } from '../../api/queries';
import { getDiscoverAvatarUrl } from '../../utils/avatar';
import { Avatar } from '../ui/Avatar';

interface AuthGateModalProps {
  icon: ReactNode;
  title: string;
  description: string;
  proofText: string;
  ctaLabel: string;
  onClose: () => void;
}

const MAX_PROOF_AVATARS = 4;

function dedupeById(users: UserDiscoverModel[]): UserDiscoverModel[] {
  const seen = new Set<string | number>();
  const result: UserDiscoverModel[] = [];
  for (const user of users) {
    if (seen.has(user.id)) continue;
    seen.add(user.id);
    result.push(user);
  }
  return result;
}

/** Shown in place of a bare sign-in redirect when a guest triggers an account-gated action. */
export function AuthGateModal({
  icon,
  title,
  description,
  proofText,
  ctaLabel,
  onClose,
}: AuthGateModalProps) {
  const { data: homeData } = useGetHomeData();
  const proofUsers = dedupeById([
    ...(homeData?.djs_artists ?? []),
    ...(homeData?.organizers ?? []),
  ]).slice(0, MAX_PROOF_AVATARS);
  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[420px] bg-[#111111] border border-white/10 rounded-[28px] overflow-hidden shadow-2xl"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-end p-4 border-b border-white/8">
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="px-7 pb-7 pt-2 flex flex-col items-center text-center">
          <div className="relative w-28 h-28 flex items-center justify-center mb-6 mt-2">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(59,130,246,0.35) 0%, rgba(59,130,246,0) 70%)',
              }}
            />
            <div className="relative w-20 h-20 rounded-full bg-black border border-white/10 flex items-center justify-center overflow-hidden">
              {icon}
            </div>
          </div>

          <h3 className="text-white text-2xl font-bold mb-3">{title}</h3>
          <p className="text-zinc-400 text-[15px] leading-relaxed mb-6">{description}</p>

          <div className="w-full bg-zinc-900/70 border border-white/8 rounded-2xl p-4 mb-6">
            {proofUsers.length > 0 && (
              <div className="flex items-center justify-center -space-x-2 mb-3">
                {proofUsers.map((user) => (
                  <Avatar
                    key={user.id}
                    src={getDiscoverAvatarUrl(user) ?? undefined}
                    name={user.full_name || user.username || 'User'}
                    size="sm"
                    className="border-2 border-zinc-900"
                  />
                ))}
              </div>
            )}
            <p className="text-zinc-400 text-[13px] italic leading-relaxed">{proofText}</p>
          </div>

          <Link
            to="/sign-up"
            className="w-full inline-flex items-center justify-center px-5 py-3.5 rounded-full bg-white text-black text-[15px] font-semibold hover:bg-zinc-200 transition-colors"
          >
            {ctaLabel}
          </Link>

          <p className="text-zinc-500 text-[13px] mt-4">
            Already have an account?{' '}
            <Link to="/sign-in" className="text-blue-400 hover:text-blue-300 font-medium">
              Sign In
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
